import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Store, Receipt, Settings2, Sun, Moon, Save, CreditCard, Building2, Shield, Activity } from 'lucide-react';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/page-header';
import { useToast } from '@/hooks/use-toast';
import { useSettingsStore } from '@/store/settings-store';
import { PaymentMethodsTab } from '@/components/settings/payment-methods-tab';
import { OutletsTab } from '@/components/settings/outlets-tab';
import { SecurityTab } from '@/components/settings/security-tab';
import { ResilienceTab } from '@/components/settings/resilience-tab';

export const Route = createFileRoute('/settings')({
  component: () => (
    <ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}>
      <MainLayout>
        <SettingsPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function SettingsPage() {
  const { toast } = useToast();
  const { storeInfo, receipt, app, updateStoreInfo, updateReceipt, updateApp } =
    useSettingsStore();

  useEffect(() => {
    if (app.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [app.theme]);

  const handleSaveStoreInfo = () => {
    toast({
      title: 'Store Information Saved',
      description: 'Your store details have been updated.',
    });
  };

  const handleSaveReceipt = () => {
    toast({
      title: 'Receipt Settings Saved',
      description: 'Your receipt configuration has been updated.',
    });
  };

  const handleSaveApp = () => {
    toast({
      title: 'Application Settings Saved',
      description: 'Your preferences have been updated.',
    });
  };

  const formatCurrencyPreview = (amount: number) => {
    if (app.currencyFormat === 'id-ID') {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(amount);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Settings"
        description="Manage your application preferences and configurations."
      />

          <Tabs defaultValue="store" className="w-full">
            <TabsList className="relative z-10 grid h-auto w-full grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-1 bg-muted/50 p-1">
              <TabsTrigger value="store" className="flex items-center gap-2">
                <Store className="h-4 w-4" />
                <span className="hidden sm:inline">Store</span>
              </TabsTrigger>
              <TabsTrigger value="receipt" className="flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                <span className="hidden sm:inline">Receipt</span>
              </TabsTrigger>
              <TabsTrigger value="application" className="flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                <span className="hidden sm:inline">App</span>
              </TabsTrigger>
              <TabsTrigger value="payment-methods" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                <span className="hidden sm:inline">Payments</span>
              </TabsTrigger>
              <TabsTrigger value="outlets" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <span className="hidden sm:inline">Outlets</span>
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">Security</span>
              </TabsTrigger>
              <TabsTrigger value="resilience" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                <span className="hidden sm:inline">Resilience</span>
              </TabsTrigger>
            </TabsList>

            {/* Store Information Tab */}
            <TabsContent value="store">
              <Card>
                <CardHeader>
                  <CardTitle>Store Information</CardTitle>
                  <CardDescription>
                    Basic information about your laundry store.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="storeName">Store Name</Label>
                    <Input
                      id="storeName"
                      placeholder="e.g., Fresh & Clean Laundry"
                      value={storeInfo.storeName}
                      onChange={(e) =>
                        updateStoreInfo({ storeName: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="storeAddress">Address</Label>
                    <Textarea
                      id="storeAddress"
                      placeholder="e.g., Jl. Sudirman No. 123, Jakarta"
                      value={storeInfo.address}
                      onChange={(e) =>
                        updateStoreInfo({ address: e.target.value })
                      }
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="storePhone">Phone Number</Label>
                      <Input
                        id="storePhone"
                        placeholder="e.g., 021-1234567"
                        value={storeInfo.phone}
                        onChange={(e) =>
                          updateStoreInfo({ phone: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="storeEmail">Email</Label>
                      <Input
                        id="storeEmail"
                        type="email"
                        placeholder="e.g., info@freshclean.com"
                        value={storeInfo.email}
                        onChange={(e) =>
                          updateStoreInfo({ email: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-end">
                    <Button onClick={handleSaveStoreInfo} className="w-full sm:w-auto">
                      <Save className="mr-2 h-4 w-4" />
                      Save Store Info
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Receipt Settings Tab */}
            <TabsContent value="receipt">
              <Card>
                <CardHeader>
                  <CardTitle>Receipt Settings</CardTitle>
                  <CardDescription>
                    Customize what appears on printed receipts.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="headerText">Header Text</Label>
                    <Textarea
                      id="headerText"
                      placeholder="Text displayed at the top of receipts"
                      value={receipt.headerText}
                      onChange={(e) =>
                        updateReceipt({ headerText: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="footerText">Footer Text</Label>
                    <Textarea
                      id="footerText"
                      placeholder="e.g., Thank you for your business!"
                      value={receipt.footerText}
                      onChange={(e) =>
                        updateReceipt({ footerText: e.target.value })
                      }
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Show Logo on Receipt</Label>
                      <p className="text-sm text-muted-foreground">
                        Display your store logo at the top of each receipt.
                      </p>
                    </div>
                    <Button
                      variant={receipt.showLogo ? 'default' : 'outline'}
                      size="sm"
                      onClick={() =>
                        updateReceipt({ showLogo: !receipt.showLogo })
                      }
                    >
                      {receipt.showLogo ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>

                  <Separator />

                  <div className="flex justify-end">
                    <Button onClick={handleSaveReceipt} className="w-full sm:w-auto">
                      <Save className="mr-2 h-4 w-4" />
                      Save Receipt Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Application Settings Tab */}
            <TabsContent value="application">
              <Card>
                <CardHeader>
                  <CardTitle>Application Settings</CardTitle>
                  <CardDescription>
                    Customize the look and behavior of the application.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <Label>Theme</Label>
                    <p className="text-sm text-muted-foreground">
                      Choose between light and dark mode.
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button
                        variant={app.theme === 'light' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateApp({ theme: 'light' })}
                        className="flex items-center gap-2"
                      >
                        <Sun className="h-4 w-4" />
                        Light
                      </Button>
                      <Button
                        variant={app.theme === 'dark' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateApp({ theme: 'dark' })}
                        className="flex items-center gap-2"
                      >
                        <Moon className="h-4 w-4" />
                        Dark
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <Label>Currency Format</Label>
                    <p className="text-sm text-muted-foreground">
                      Select how prices are displayed throughout the app.
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button
                        variant={
                          app.currencyFormat === 'id-ID' ? 'default' : 'outline'
                        }
                        size="sm"
                        onClick={() => updateApp({ currencyFormat: 'id-ID' })}
                      >
                        IDR (Rp)
                      </Button>
                      <Button
                        variant={
                          app.currencyFormat === 'en-US' ? 'default' : 'outline'
                        }
                        size="sm"
                        onClick={() => updateApp({ currencyFormat: 'en-US' })}
                      >
                        USD ($)
                      </Button>
                    </div>
                    <div className="rounded-md border p-3 bg-muted/50">
                      <p className="text-sm text-muted-foreground">Preview:</p>
                      <p className="text-lg font-semibold">
                        {formatCurrencyPreview(150000)}
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-end">
                    <Button onClick={handleSaveApp} className="w-full sm:w-auto">
                      <Save className="mr-2 h-4 w-4" />
                      Save Application Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payment Methods Tab */}
            <TabsContent value="payment-methods">
              <PaymentMethodsTab />
            </TabsContent>

            {/* Outlets Tab */}
            <TabsContent value="outlets">
              <OutletsTab />
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security">
              <SecurityTab />
            </TabsContent>

            {/* Resilience Tab */}
            <TabsContent value="resilience">
              <ResilienceTab />
            </TabsContent>
          </Tabs>
        </div>
  );
}
